import { chatAIService, GET_CONVERSATION_INFO, GET_CONVERSATIONS } from '@/api';
import { ChatMessagesContext } from '@/context';
import { ChatMessagesProps, ChatRole, ChatAIStreamRequest } from '@/interfaces';
import { parserUserMessage } from '@/lib';
import { getChatErrorMessage } from '@/lib/chatErrorMessage';
import {
	deleteLocalStorage,
	getLocalStorage,
	setLocalStorage,
} from '@/utils/localStorage';
import { useSession } from 'next-auth/react';
import { chatAIStreamService } from '@/api/chatAIStream';
import * as React from 'react';
import { useRouter } from 'next/router';
import { format } from 'date-fns';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { ConversationList } from '@/interfaces/inbox';
import { inboxService } from '@/api/inbox';
import Icons from '@/const/icons';

export const useChatMessages = () => {
	const router = useRouter();
	const { id: conversationId, ai_enabled } = router.query;
	const isChatAI = ai_enabled === 'true';

	const {
		chatMessages,
		setChatMessages,
		isLoading,
		setIsLoading,
		isConversationOver,
		setIsConversationOver,
		date,
		setDate,
	} = React.useContext(ChatMessagesContext);

	const { data: session } = useSession();
	const userId = session?.user.user.id;
	const user = session?.user.user;

	const [streamingMessageId, setStreamingMessageId] = React.useState<
		string | null
	>(null);
	const [currentBotMessage, setCurrentBotMessage] = React.useState('');
	const [threadId, setThreadId] = React.useState<string | null>(null);

	React.useEffect(() => {
		if (userId) {
			const savedThreadId = getLocalStorage(`threadId_${userId}`);
			if (savedThreadId) {
				setThreadId(savedThreadId);
			}
		}
	}, [userId]);

	const fetchData = async ({
		pageParam = 1,
	}: {
		pageParam?: number;
	}): Promise<ConversationList> => {
		const response = await inboxService.getConversations({
			caseId: '',
			clientId: '',
			conversationId: (conversationId as string) || '',
			page: pageParam,
			limit: 100,
		});
		return response.data;
	};

	const {
		data: conversationPaginated,
		isLoading: isLoadingConversation,
		refetch: refetchConversation,
		// fetchNextPage,
		// hasNextPage,
		// isFetchingNextPage,
		// isError,
	} = useInfiniteQuery({
		initialData: undefined,
		initialPageParam: 1,
		queryKey: [GET_CONVERSATIONS, conversationId, isChatAI],
		queryFn: fetchData,
		getNextPageParam(lastPage) {
			return lastPage.page < lastPage.totalPages
				? lastPage.page + 1
				: undefined;
		},
		enabled: !!conversationId && isChatAI,
	});

	const { data: conversationInfo } = useQuery({
		queryKey: [GET_CONVERSATION_INFO, conversationId],
		queryFn: () =>
			inboxService.getConversationInfo((conversationId as string) || ''),
		enabled: !!conversationId && isChatAI,
		refetchOnWindowFocus: false,
	});

	const conversationMessages: ChatMessagesProps[] = React.useMemo(() => {
		return (
			conversationPaginated?.pages
				.flatMap((page) => page.items)
				.map((message) => {
					const role = message.role as ChatRole;
					return {
						message: message.content,
						role: role,
						timestamp: message.createdAt,
						id: message.id,
						first_name: message.firstname,
						last_name: message.lastname,
						profile_picture:
							message.role === 'bot'
								? Icons.hatAvatar
								: message.profile_picture,
						action_required_id: message.action_required_id,
						document_url: message.document_url,
						document_name: message.document_name,
						action_required_complete: message.action_required_complete,
					};
				}) || []
		);
	}, [conversationPaginated]);

	const postChatMessage = async (message: string) => {
		const res = await chatAIService.postChatMessage({ content: message });
		return res.data;
	};

	const handleStreamingResponse = async (
		message: string,
		messagesAux: ChatMessagesProps[],
		currentThreadId: string | null,
		actionRequiredId?: number,
		documentUrl?: string
	): Promise<void> => {
		try {
			const botMessageId = (Date.now() + 1).toString();
			setStreamingMessageId(botMessageId);
			setCurrentBotMessage('');

			const timestamp = format(new Date(), 'MM/dd/yyyy, h:mm a');

			const initialBotMessage: ChatMessagesProps = {
				message: '',
				role: 'system',
				timestamp,
				id: botMessageId,
				profile_picture: Icons.hatAvatar,
				first_name: 'ClaimMagic',
				last_name: 'AI',
			};

			let currentMessages = [...messagesAux, initialBotMessage];
			setChatMessages(currentMessages);

			const requestBody: ChatAIStreamRequest = {
				content: message,
				inbox: isChatAI,
				conversation_id: (conversationId as string) || '',
			};

			if (currentThreadId) requestBody.thread_id = currentThreadId;
			if (actionRequiredId) requestBody.action_required_id = actionRequiredId;
			if (documentUrl) requestBody.document_url = documentUrl;

			// add if the conversation is from AI
			if (conversationInfo?.data.case.id && isChatAI)
				requestBody.case_id = conversationInfo?.data.case.id;
			if (conversationInfo?.data.applicationId && isChatAI)
				requestBody.application_id = String(
					conversationInfo?.data.applicationId
				);

			await chatAIStreamService.streamChatMessage(requestBody, {
				onStart: (data) => {
					if (data.thread_id) {
						setThreadId(data.thread_id);
						setLocalStorage(`threadId_${userId}`, data.thread_id);
					}
				},
				onContent: (accumulated) => {
					setCurrentBotMessage(accumulated);

					const updatedMessages = currentMessages.map((msg) =>
						msg.id === botMessageId ? { ...msg, message: accumulated } : msg
					);

					currentMessages = updatedMessages;
					setChatMessages(updatedMessages);
				},
				onDone: (finalData) => {
					if (finalData?.thread_id) {
						setThreadId(finalData.thread_id);
						setLocalStorage(`threadId_${userId}`, finalData.thread_id);
					}

					const finalMessages = currentMessages.map((msg) =>
						msg.id === botMessageId
							? {
									...msg,
									message:
										finalData.fullMessage +
										`<div class="chat-timestamp"><span>${timestamp}</span></div>`,
							  }
							: msg
					);

					setChatMessages(finalMessages);
					if (!isChatAI) {
						setLocalStorage(`chatMessages_${userId}`, finalMessages);
					}
					setStreamingMessageId(null);
					setCurrentBotMessage('');
					checkIfConversationEnds(finalData.fullMessage);
				},
				onError: (err) => {
					console.error('Streaming error:', err);
					throw err;
				},
			});
		} catch (error) {
			console.error('Streaming error (outer catch):', error);
			throw error;
		}
	};

	const addChatMessages = async (
		message: string,
		role: ChatRole,
		customThreadId?: string | null,
		actionRequiredId?: number,
		documentUrl?: string
	): Promise<void> => {
		setIsLoading(true);
		const messagesAux: ChatMessagesProps[] = isChatAI
			? [...chatMessages]
			: getLocalStorage(`chatMessages_${userId}`) || [];

		try {
			if (role === 'user') {
				messagesAux.push({
					message: parserUserMessage(message, 'user'),
					role: role,
					profile_picture: user?.profile_picture,
					first_name: user?.firstname,
					last_name: user?.lastname,
				});
				setChatMessages(messagesAux);
				if (!isChatAI) {
					setLocalStorage(`chatMessages_${userId}`, messagesAux);
				}
			}

			const currentThreadId =
				customThreadId !== undefined ? customThreadId : threadId;

			await handleStreamingResponse(
				message,
				messagesAux,
				currentThreadId,
				actionRequiredId,
				documentUrl
			);
		} catch (error) {
			console.error({ error });
			const chatError = getChatErrorMessage();
			messagesAux.push(chatError);
			setChatMessages(messagesAux);
			if (!isChatAI) {
				setLocalStorage(`chatMessages_${userId}`, messagesAux);
			}
		} finally {
			setIsLoading(false);
			setStreamingMessageId(null);
			setCurrentBotMessage('');
		}
	};

	const addHelloMessage = (): void => {
		if (isChatAI) return;

		const messagesAux: ChatMessagesProps[] =
			getLocalStorage(`chatMessages_${userId}`) || [];
		if (messagesAux.length === 0) {
			messagesAux.push({
				message: parserUserMessage('Hello! How can I help you?', 'system'),
				role: 'system',
				profile_picture: Icons.hatAvatar,
				first_name: 'ClaimMagic',
				last_name: 'AI',
			});
			setLocalStorage(`chatMessages_${userId}`, messagesAux);
			setDate(new Date().toString());
			setLocalStorage(`initialChatDate_${userId}`, new Date());
		}
	};

	const removeButtonMessages = (currentMessages: ChatMessagesProps[]) => {
		const auxChatMessages = currentMessages;
		const index = auxChatMessages.length - 1;

		const messageSplitted = auxChatMessages[index].message.split(
			'<div className="grid grid-cols-2 gap-3">'
		);

		auxChatMessages[index].message = messageSplitted[0];
		return auxChatMessages;
	};

	const resetContext = async () => {
		setChatMessages([]);
		setStreamingMessageId(null);
		setCurrentBotMessage('');
		setThreadId(null);
		deleteLocalStorage(`chatMessages_${userId}`);
		deleteLocalStorage(`isConversationOver_${userId}`);
		deleteLocalStorage(`showChatBox_${userId}`);
		deleteLocalStorage(`initialChatDate_${userId}`);
		deleteLocalStorage(`threadId_${userId}`);
		await postChatMessage('conversation-end');
		uploadChat();
	};

	const checkIfConversationEnds = (message: string) => {
		if (
			message.includes("I don't have the information") ||
			message.includes('Thank you for chatting with us.') ||
			message.includes('conversation-end')
		) {
			setIsConversationOver(true);
			setLocalStorage(`isConversationOver_${userId}`, true);
		}
	};

	const uploadChat = () => {
		if (isChatAI) {
			setChatMessages(conversationMessages);
		} else {
			const messagesAux: ChatMessagesProps[] =
				getLocalStorage(`chatMessages_${userId}`) || [];
			setChatMessages(messagesAux);
			setIsConversationOver(
				getLocalStorage(`isConversationOver_${userId}`) || false
			);
		}
		setDate(getLocalStorage(`initialChatDate_${userId}`) || new Date());

		const savedThreadId = getLocalStorage(`threadId_${userId}`);
		if (savedThreadId) {
			setThreadId(savedThreadId);
		}
	};

	React.useEffect(() => {
		uploadChat();
	}, [isChatAI, conversationMessages]);

	return {
		chatMessages,
		setChatMessages,
		addChatMessages,
		addHelloMessage,
		resetContext,
		isLoading,
		setIsLoading,
		isConversationOver,
		date,
		removeButtonMessages,
		streamingMessageId,
		currentBotMessage,
		threadId,
		isChatAI,
		user,
		conversationInfo,
		isLoadingConversation,
		refetchConversation,
	};
};
